import { useCompactNumberFormatter } from '@shared/hooks/useCompactNumberFormatter';
import { AccommodationsConsoleState, JoyHotelAccommodationListingDTO, RawJoyHotelPricingInfo, StandardHotelPricingValues } from './AccommodationsConsole.types';
import { StaysSearchTerms } from '@apps/stays/types';
import { isValidLatLngLocation } from '@shared/utils/locationValidation';
import { AccommodationsConsolePropertiesV2Query } from '@graphql/generated';
import { extractHotelRedirects } from '@shared/utils/extractHotelRedirects';

export type AccommodationConsoleQueryProperty = AccommodationsConsolePropertiesV2Query['searchExtendedAccommodationProperties']['properties'][number];

export type ValidAccommodationConsoleQueryProperty = Merge<
  AccommodationConsoleQueryProperty,
  { readonly location: { readonly latitude: number; readonly longitude: number }; readonly address: NonNullable<AccommodationConsoleQueryProperty['address']> }
>;

export const isValidProperty = (property: AccommodationConsoleQueryProperty): property is ValidAccommodationConsoleQueryProperty => {
  return isValidLatLngLocation(property.location) && !!property.address;
};

export const transformPropertiesToListings = (
  properties: ValidAccommodationConsoleQueryProperty[],
  mapHotelRoomPrice: (pricingInfo: RawJoyHotelPricingInfo) => StandardHotelPricingValues,
): JoyHotelAccommodationListingDTO[] => {
  return properties
    .sort((a, b) => b.score - a.score)
    .map(listing => ({
      userNote: null,
      id: listing.id,
      description: null,
      isFavorite: false,
      name: listing.name,
      searchScore: listing.score,
      joyPlaceId: listing.joyPlaceId,
      type: 'standardHotel' as const,
      facilities: listing.facilities,
      address: {
        city: listing.address.city,
        street: listing.address.street,
        country: listing.address.country,
      },
      otaProvider: listing.otaProvider,
      location: {
        lat: listing.location.latitude,
        lng: listing.location.longitude,
      },
      starRating: listing.starRating,
      userRating: listing.userRating
        ? {
            ratingValue: listing.userRating.ratingValue,
            ratingCount: listing.userRating.ratingCount,
          }
        : null,
      mediaAssets: {
        isLoading: false,
        photos: listing.photos,
      },
      pricingInfo: {
        isLoading: false,
        values: listing.pricingInfo ? mapHotelRoomPrice(listing.pricingInfo) : null,
      },
      redirectMechanisms: listing.redirectMechanisms || undefined,
      isSoldOutForSelectedDates: !!extractHotelRedirects(listing.redirectMechanisms)?.hotelSearchPageUrl,
    }));
};

/**
 * Hook for formatting review counts in a compact way (e.g., 1000 -> 1K) using the current locale.
 */
export const useReviewCountFormatter = () => {
  return useCompactNumberFormatter().formatCompactNumber;
};

/**
 * Formats a user rating number to one decimal place.
 *
 * eg. 8.5 -> "8.5", 9 -> "9.0"
 */
export const formatUserRatingNumber = (rating: number): string => {
  return rating.toFixed(1);
};

export type ParamsUpdater = (key: string, value: string | null) => void;

export const createQueryParamsUpdater =
  (params: URLSearchParams): ParamsUpdater =>
  (key, value) => {
    if (value === null) {
      params.delete(key);
    } else {
      params.set(key, value);
    }
  };

export const getSearchFormInitialValue = (state: AccommodationsConsoleState): Partial<StaysSearchTerms> => {
  return {
    destination: state.searchLocation,
    lat: state.circularRegionSearch?.latitude,
    lng: state.circularRegionSearch?.longitude,
    startDate: {
      year: parseInt(state.searchDates.checkin.split('-')[0]),
      month: parseInt(state.searchDates.checkin.split('-')[1]),
      day: parseInt(state.searchDates.checkin.split('-')[2]),
    },
    endDate: {
      year: parseInt(state.searchDates.checkout.split('-')[0]),
      month: parseInt(state.searchDates.checkout.split('-')[1]),
      day: parseInt(state.searchDates.checkout.split('-')[2]),
    },
    rooms:
      state.occupancies?.occupancies?.map(occ => ({
        adults: occ.adults,
        childrenAges: (occ.childrenAges || []).filter((age): age is number => age !== null),
      })) || [],
  };
};
